import { ChangeEvent, FormEvent, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { apiFetch, getAuthHeaders } from "../../lib/api"
import { redirectToLoginOnUnauthorized } from "../../lib/auth"
import { useEmpresa } from "../../app/context/EmpresaContext"
import { config } from "../../lib/config"

const PAGE_SIZE = 10

const Spinner = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg
    className={`animate-spin text-current ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
)

const CopyIcon = ({ className = "h-3.5 w-3.5" }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.75}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-6c-1.036 0-1.875.84-1.875 1.875v6c0 1.035.84 1.875 1.875 1.875h6a2.25 2.25 0 0 0 2.166-1.638M18.75 8.25v6.375A2.625 2.625 0 0 1 16.125 17.25h-6a2.625 2.625 0 0 1-2.625-2.625V8.25"
    />
  </svg>
)

const RefreshIcon = ({ className = "h-3.5 w-3.5" }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
)

const tempoRelativo = (iso?: string | null) => {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000)
  if (diffSec < 60) return `há ${Math.max(1, diffSec)} s`
  if (diffSec < 3600) return `há ${Math.round(diffSec / 60)} min`
  if (diffSec < 86400) return `há ${Math.round(diffSec / 3600)} h`
  return formatWhen(iso)
}

type NfeList = {
  items: NfeDoc[]
  total: number
  page: number
  page_size: number
  fila_ambiente?: {
    notas_na_fila?: number
    jobs_pendentes?: number
    jobs_processando?: number
  } | null
}

type NfeEventoFiscal = {
  tipo: "cancelamento" | "cce" | string
  status: string
  motivo?: string | null
  evento_id?: string
  protocolo?: string | null
  sequencia?: number | null
  criado_em?: string | null
  justificativa?: string | null
}

type InutilizacaoItem = {
  id: string
  serie: number
  numero_inicial: number
  numero_final: number
  ano: number
  ambiente?: number
  status: string
  protocolo?: string | null
  justificativa?: string | null
  criado_em?: string | null
}

const inutilizacaoStatusLabel = (status: string) => {
  if (status === "REGISTRADO") return "Inutilizada"
  if (status === "ENFILEIRADO" || status === "PENDENTE") return "Na fila"
  if (status === "REJEITADO") return "Rejeitada"
  return status
}

type NfeDoc = {
  id: string
  status: string
  ambiente?: number
  chave_acesso?: string | null
  numero: number
  serie: number
  codigo_status_sefaz?: string | null
  motivo_status_sefaz?: string | null
  protocolo?: string | null
  criado_em?: string | null
  processado_em?: string | null
  job_fila?: "pendente" | "processando" | "aguardando_worker" | null
  pode_reenviar?: boolean
  evento_fiscal?: NfeEventoFiscal | null
  evento_fila?: "pendente" | "processando" | null
  evento_job?: { status?: string; erro?: string | null; locked_at?: string | null } | null
}

const formatWhen = (iso?: string | null) => {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" })
}

const filaJobLabel = (jobFila?: NfeDoc["job_fila"]) => {
  if (jobFila === "pendente") return "Na fila de processamento"
  if (jobFila === "processando") return "Processando"
  if (jobFila === "aguardando_worker") return "Aguardando processamento"
  return null
}

const motivoEventoUsuario = (motivo?: string | null) => {
  if (!motivo?.trim()) return null
  const m = motivo.trim()
  if (
    /bug corrigido|worker|API\/worker|atualize API|interpretou o código do lote|iniciar o/i.test(m)
  )
    return null
  if (/^\[128\]/i.test(m) || /^lote de evento processado$/i.test(m)) return null
  return m
}

const filaJobClass = (jobFila?: NfeDoc["job_fila"]) => {
  if (jobFila === "processando") return "bg-sky-100 text-sky-900"
  if (jobFila === "pendente" || jobFila === "aguardando_worker") return "bg-amber-100 text-amber-950"
  return ""
}

const ambienteTabBadge = (
  porAmbiente: Record<string, { notas_na_fila?: number; jobs_pendentes?: number; jobs_processando?: number }> | undefined,
  ambiente: 1 | 2,
) => {
  const stats = porAmbiente?.[String(ambiente)]
  if (!stats) return null
  const total =
    (stats.notas_na_fila ?? 0) + (stats.jobs_pendentes ?? 0) + (stats.jobs_processando ?? 0)
  if (total <= 0) return null
  return total
}
const ambienteLabel = (ambiente?: number) => (ambiente === 1 ? "Produção" : "Homologação")

const statusClass = (status: string) => {
  if (status === "AUTORIZADO" || status === "REGISTRADO") return "bg-emerald-100 text-emerald-800"
  if (status === "CANCELADO") return "bg-slate-200 text-slate-800 line-through decoration-slate-500"
  if (status === "DENEGADO") return "bg-orange-100 text-orange-900"
  if (status === "REJEITADO" || status === "ERRO" || status === "FALHA_COMUNICACAO") return "bg-red-100 text-red-800"
  if (status === "PROCESSANDO" || status === "ENFILEIRADO") return "bg-amber-100 text-amber-900"
  return "bg-slate-100 text-slate-700"
}

const statusLabel = (doc: NfeDoc) => {
  if (doc.status === "CANCELADO") return "Cancelada"
  if (doc.status === "AUTORIZADO") return "Autorizada"
  if (doc.status === "REJEITADO") return "Rejeitada"
  if (doc.status === "FALHA_COMUNICACAO") return "Falha de comunicação"
  if (doc.status === "ERRO") return "Erro"
  if (doc.status === "DENEGADO") return "Denegada"
  return doc.status
}

const cancelamentoDetalhes = (doc: NfeDoc) => {
  if (doc.status !== "CANCELADO") return null
  const ev = doc.evento_fiscal?.tipo === "cancelamento" ? doc.evento_fiscal : null
  return {
    protocolo: ev?.protocolo || doc.protocolo || null,
    quando: ev?.criado_em || doc.processado_em || null,
    justificativa: ev?.justificativa || null,
    statusEvento: ev?.status || null,
    motivoSefaz: doc.motivo_status_sefaz || null,
  }
}

const eventoFiscalResumo = (doc: NfeDoc) => {
  const ev = doc.evento_fiscal
  if (!ev) return null
  if (doc.status === "CANCELADO") {
    if (ev.tipo === "cancelamento") {
      return { tone: "ok" as const, text: "NF-e cancelada" }
    }
    return null
  }
  if (ev.tipo === "cancelamento" && ev.status === "ENFILEIRADO") {
    if (doc.evento_job?.status === "dead") {
      const err = motivoEventoUsuario(doc.evento_job.erro || ev.motivo)
      return {
        tone: "err" as const,
        text: err ? `Cancelamento não concluído: ${err}` : "Cancelamento não concluído. Tente novamente.",
      }
    }
    if (doc.evento_fila === "processando") {
      return { tone: "wait" as const, text: "Cancelamento em processamento…" }
    }
    return { tone: "wait" as const, text: "Cancelamento em fila…" }
  }
  if (ev.tipo === "cancelamento" && ev.status === "REJEITADO") {
    const motivo = motivoEventoUsuario(ev.motivo)
    if (!motivo) {
      return {
        tone: "wait" as const,
        text: "Situação do cancelamento indeterminada — use Consultar SEFAZ.",
      }
    }
    return { tone: "err" as const, text: `Cancelamento não aceito: ${motivo}` }
  }
  if (ev.tipo === "cancelamento" && ev.status === "REGISTRADO" && doc.status !== "CANCELADO") {
    return { tone: "wait" as const, text: "Cancelamento registrado — use Consultar SEFAZ para atualizar a nota." }
  }
  if (ev.tipo === "cce" && ev.status === "ENFILEIRADO") {
    return { tone: "wait" as const, text: "Carta de correção enfileirada" }
  }
  if (ev.tipo === "cce" && ev.status === "REGISTRADO") {
    return { tone: "ok" as const, text: "CC-e registrada" }
  }
  return null
}

const cancelamentoPendente = (doc: NfeDoc) =>
  doc.evento_fiscal?.tipo === "cancelamento" &&
  doc.evento_fiscal.status === "ENFILEIRADO" &&
  doc.evento_job?.status !== "dead"

const fileStem = (doc: NfeDoc) => {
  const chave = (doc.chave_acesso || "").replace(/\D/g, "")
  if (chave.length === 44) return chave
  return `${doc.serie}-${String(doc.numero).padStart(9, "0")}`
}

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const parseApiError = (err: unknown, fallback = "Operação falhou") => {
  if (!(err instanceof Error)) return fallback
  try {
    const parsed = JSON.parse(err.message) as { detail?: string | Array<{ msg?: string }> }
    if (typeof parsed.detail === "string") return parsed.detail
    if (Array.isArray(parsed.detail)) {
      const parts = parsed.detail.map((item) => item.msg).filter(Boolean)
      if (parts.length) return parts.join("; ")
    }
  } catch {
    /* texto bruto */
  }
  return err.message || fallback
}

type NfeFilaResumo = {
  backend: string
  worker_intervalo_segundos: number | null
  worker_parece_ativo: boolean | null
  ultimo_job_concluido_em?: string | null
  jobs_pendentes: { total: number | null; desta_empresa: number | null }
  jobs_processando: { total: number | null; desta_empresa: number | null }
  notas_aguardando_worker?: number | null
  mensagem?: string
  por_ambiente?: Record<
    string,
    { notas_na_fila?: number; jobs_pendentes?: number; jobs_processando?: number }
  >
}

type NfeContexto = {
  sefaz_simulado: boolean
  uf_suportada: string
  empresa_uf_ok: boolean
  certificado_hom: boolean
  certificado_prod: boolean
  fila?: NfeFilaResumo
  empresa: {
    razao_social: string
    nome_fantasia: string | null
    cnpj: string
    uf: string
  }
}

export const NfePage = () => {
  const { empresaId } = useEmpresa()
  const [docs, setDocs] = useState<NfeDoc[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [ambienteFiltro, setAmbienteFiltro] = useState<1 | 2>(1)
  const [ctx, setCtx] = useState<NfeContexto | null>(null)
  const [loading, setLoading] = useState(false)
  const [submittingDemo, setSubmittingDemo] = useState(false)
  const [submittingXml, setSubmittingXml] = useState(false)
  const [message, setMessage] = useState("")
  const [isError, setIsError] = useState(false)
  const [xmlFile, setXmlFile] = useState<File | null>(null)
  const [zipDe, setZipDe] = useState("")
  const [zipAte, setZipAte] = useState("")
  const [zipIncluir, setZipIncluir] = useState<"ambos" | "xml" | "danfe">("ambos")
  const [filaAmbienteListagem, setFilaAmbienteListagem] = useState<NfeList["fila_ambiente"]>(null)
  const [atualizandoAmbiente, setAtualizandoAmbiente] = useState<1 | 2 | null>(null)
  const [cancelTarget, setCancelTarget] = useState<NfeDoc | null>(null)
  const [cancelJustificativa, setCancelJustificativa] = useState("")
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [copiedChaveId, setCopiedChaveId] = useState<string | null>(null)
  const [reenviandoId, setReenviandoId] = useState<string | null>(null)
  const [inutilizacoes, setInutilizacoes] = useState<InutilizacaoItem[]>([])
  const [loadingInutilizacoes, setLoadingInutilizacoes] = useState(false)
  const [submittingInutilizacao, setSubmittingInutilizacao] = useState(false)

  const loadDocs = async (opts?: { silent?: boolean; ambiente?: 1 | 2; page?: number }) => {
    if (!empresaId) return
    const amb = opts?.ambiente ?? ambienteFiltro
    const pg = opts?.page ?? page
    if (!opts?.silent) setLoading(true)
    try {
      const params = new URLSearchParams({
        ambiente: String(amb),
        page: String(pg),
        page_size: String(PAGE_SIZE),
      })
      const data = await apiFetch<NfeList>(`/v1/empresas/${empresaId}/nfe?${params}`)
      setDocs(data.items ?? [])
      setTotal(data.total ?? 0)
      setFilaAmbienteListagem(data.fila_ambiente ?? null)
    } catch (err) {
      setIsError(true)
      setMessage(err instanceof Error ? err.message : "Erro ao listar notas")
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }

  const afterEnfileirarDocumento = async (documento?: NfeDoc) => {
    const amb =
      documento?.ambiente === 1 || documento?.ambiente === 2 ? (documento.ambiente as 1 | 2) : ambienteFiltro
    if (documento?.ambiente === 1 || documento?.ambiente === 2) {
      setAmbienteFiltro(documento.ambiente as 1 | 2)
      setPage(1)
    }
    await loadContexto()
    await loadDocs({ silent: true, ambiente: amb, page: 1 })
  }

  const loadInutilizacoes = async (opts?: { silent?: boolean }) => {
    if (!empresaId) return
    if (!opts?.silent) setLoadingInutilizacoes(true)
    try {
      const data = await apiFetch<InutilizacaoItem[]>(`/v1/empresas/${empresaId}/nfe/inutilizacoes`)
      setInutilizacoes(data ?? [])
    } catch (err) {
      setIsError(true)
      setMessage(err instanceof Error ? err.message : "Erro ao listar inutilizações")
    } finally {
      if (!opts?.silent) setLoadingInutilizacoes(false)
    }
  }

  const loadContexto = async () => {
    if (!empresaId) {
      setCtx(null)
      return
    }
    try {
      const data = await apiFetch<NfeContexto>(`/v1/empresas/${empresaId}/nfe/contexto`)
      setCtx(data)
    } catch {
      setCtx(null)
    }
  }

  useEffect(() => {
    loadContexto()
  }, [empresaId])

  useEffect(() => {
    if (!empresaId) return
    const timer = window.setInterval(() => {
      loadContexto()
    }, 10_000)
    return () => window.clearInterval(timer)
  }, [empresaId])

  const fila = ctx?.fila
  const badgeProd = ambienteTabBadge(fila?.por_ambiente, 1)
  const badgeHom = ambienteTabBadge(fila?.por_ambiente, 2)
  const eventosAguardando = docs.some((d) => cancelamentoPendente(d) || d.evento_fiscal?.status === "ENFILEIRADO")
  const notasAguardando =
    (fila?.notas_aguardando_worker ?? 0) > 0 ||
    docs.some((d) => d.status === "ENFILEIRADO" || d.status === "PROCESSANDO") ||
    eventosAguardando
  const inutilizacoesAguardando = inutilizacoes.some((row) => row.status === "ENFILEIRADO" || row.status === "PENDENTE")

  useEffect(() => {
    if (!empresaId || (!notasAguardando && !inutilizacoesAguardando)) return
    const timer = window.setInterval(() => {
      if (notasAguardando) loadDocs({ silent: true })
      if (inutilizacoesAguardando) loadInutilizacoes({ silent: true })
      loadContexto()
    }, 5_000)
    return () => window.clearInterval(timer)
  }, [empresaId, notasAguardando, inutilizacoesAguardando, ambienteFiltro, page])

  useEffect(() => {
    loadDocs()
    loadInutilizacoes()
  }, [empresaId, ambienteFiltro, page])

  const handleAmbienteFiltro = (value: 1 | 2) => {
    setAmbienteFiltro(value)
    setPage(1)
  }

  const handleAtualizarAmbiente = async (ambiente: 1 | 2) => {
    if (!empresaId || atualizandoAmbiente !== null) return
    setAtualizandoAmbiente(ambiente)
    try {
      await loadContexto()
      if (ambienteFiltro === ambiente) {
        await loadDocs({ silent: docs.length > 0, ambiente, page })
        await loadInutilizacoes({ silent: inutilizacoes.length > 0 })
      }
    } finally {
      setAtualizandoAmbiente(null)
    }
  }

  const handleEmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!empresaId || submittingDemo) return
    setMessage("")
    setIsError(false)
    setSubmittingDemo(true)
    try {
      const resp = await apiFetch<{ reenvio?: boolean; documento?: NfeDoc }>(`/v1/empresas/${empresaId}/nfe`, {
        method: "POST",
        body: JSON.stringify({
          ambiente: 2,
          serie: 1,
          natureza_operacao: "Venda",
          destinatario: { nome: "Cliente Teste", cpf: "00000000000" },
          itens: [{ descricao: "Produto teste", ncm: "84713012", cfop: "5102", quantidade: 1, valor_unitario: 10 }],
        }),
      })
      setMessage("Nota de demonstração enfileirada. Acompanhe o status na lista abaixo.")
      await afterEnfileirarDocumento(resp.documento)
    } catch (err) {
      setIsError(true)
      setMessage(err instanceof Error ? err.message : "Falha na emissão")
    } finally {
      setSubmittingDemo(false)
    }
  }

  const handleXmlFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setXmlFile(e.target.files?.[0] ?? null)
  }

  const handleEmitXml = async (e: FormEvent) => {
    e.preventDefault()
    if (!empresaId || !xmlFile || submittingXml) return
    setMessage("")
    setIsError(false)
    setSubmittingXml(true)
    try {
      const xml = await xmlFile.text()
      const resp = await apiFetch<{ reenvio?: boolean; documento?: NfeDoc }>(`/v1/empresas/${empresaId}/nfe/xml`, {
        method: "POST",
        body: JSON.stringify({ xml }),
      })
      setMessage(
        resp.reenvio
          ? "XML atualizado na mesma nota (série/número) e reenfileirado para autorização."
          : "XML recebido. A nota será assinada e enviada à SEFAZ em instantes.",
      )
      setXmlFile(null)
      await afterEnfileirarDocumento(resp.documento)
    } catch (err) {
      setIsError(true)
      setMessage(parseApiError(err))
    } finally {
      setSubmittingXml(false)
    }
  }

  const downloadXml = async (doc: NfeDoc) => {
    if (!empresaId) return
    const res = await fetch(`${config.apiUrl}/v1/empresas/${empresaId}/nfe/${doc.id}/xml`, {
      headers: getAuthHeaders(),
    })
    if (res.status === 401) {
      redirectToLoginOnUnauthorized()
      return
    }
    if (!res.ok) {
      setIsError(true)
      setMessage(parseApiError(new Error(await res.text()), "Não foi possível baixar o XML."))
      return
    }
    saveBlob(await res.blob(), `${fileStem(doc)}.xml`)
  }

  const handleReenviar = async (doc: NfeDoc) => {
    if (!empresaId || reenviandoId) return
    setReenviandoId(doc.id)
    setMessage("")
    setIsError(false)
    try {
      await apiFetch(`/v1/empresas/${empresaId}/nfe/${doc.id}/reenviar`, { method: "POST" })
      setMessage("Reenvio enfileirado. A nota será enviada de novo à SEFAZ.")
      await loadDocs()
    } catch (err) {
      setIsError(true)
      setMessage(parseApiError(err, "Não foi possível reenviar a nota."))
    } finally {
      setReenviandoId(null)
    }
  }

  const openCancelDialog = (doc: NfeDoc) => {
    setMessage("")
    setIsError(false)
    setCancelJustificativa("")
    setCancelTarget(doc)
  }

  const closeCancelDialog = () => {
    setCancelTarget(null)
    setCancelJustificativa("")
  }

  const handleConfirmCancelar = async () => {
    if (!empresaId || !cancelTarget || cancelSubmitting) return
    const justificativa = cancelJustificativa.trim()
    if (justificativa.length < 15) {
      setIsError(true)
      setMessage("A justificativa do cancelamento precisa ter no mínimo 15 caracteres.")
      return
    }
    setCancelSubmitting(true)
    setMessage("")
    setIsError(false)
    try {
      await apiFetch<{ ok?: boolean; evento_id?: string; status?: string }>(
        `/v1/empresas/${empresaId}/nfe/${cancelTarget.id}/cancelar`,
        {
          method: "POST",
          body: JSON.stringify({ justificativa }),
        },
      )
      setMessage(
        "Cancelamento solicitado. A nota continua autorizada até a SEFAZ confirmar o cancelamento.",
      )
      setIsError(false)
      closeCancelDialog()
      await loadContexto()
      await loadDocs({ silent: true })
    } catch (err) {
      setIsError(true)
      setMessage(parseApiError(err, "Não foi possível enfileirar o cancelamento"))
    } finally {
      setCancelSubmitting(false)
    }
  }

  const handleCce = async (doc: NfeDoc) => {
    if (!empresaId) return
    const correcao = window.prompt("Texto da carta de correção (mín. 15 caracteres):")
    if (!correcao || correcao.length < 15) return
    try {
      await apiFetch(`/v1/empresas/${empresaId}/nfe/${doc.id}/cce`, {
        method: "POST",
        body: JSON.stringify({ correcao }),
      })
      setMessage("Carta de correção enfileirada.")
      setIsError(false)
    } catch (err) {
      setIsError(true)
      setMessage(parseApiError(err))
    }
  }

  const handleCopyChave = async (doc: NfeDoc) => {
    const raw = doc.chave_acesso || ""
    const chave = raw.replace(/\D/g, "") || raw
    if (!chave) return
    try {
      await navigator.clipboard.writeText(chave)
      setCopiedChaveId(doc.id)
      window.setTimeout(() => {
        setCopiedChaveId((current) => (current === doc.id ? null : current))
      }, 2000)
    } catch {
      setIsError(true)
      setMessage("Não foi possível copiar a chave de acesso.")
    }
  }

  const handleSincronizar = async (doc: NfeDoc) => {
    if (!empresaId || !doc.chave_acesso) return
    try {
      await apiFetch(`/v1/empresas/${empresaId}/nfe/chave/${doc.chave_acesso}/sincronizar`, { method: "POST" })
      setMessage("Consulta à SEFAZ enfileirada.")
      setIsError(false)
      await loadDocs()
    } catch (err) {
      setIsError(true)
      setMessage(parseApiError(err))
    }
  }

  const handleInutilizar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!empresaId || submittingInutilizacao) return
    const fd = new FormData(e.currentTarget)
    const amb = Number(fd.get("ambiente"))
    const ambiente = amb === 1 || amb === 2 ? (amb as 1 | 2) : ambienteFiltro
    setSubmittingInutilizacao(true)
    try {
      await apiFetch(`/v1/empresas/${empresaId}/nfe/inutilizacoes`, {
        method: "POST",
        body: JSON.stringify({
          ambiente,
          serie: Number(fd.get("serie")),
          numero_inicial: Number(fd.get("numero_inicial")),
          numero_final: Number(fd.get("numero_final")),
          ano: Number(fd.get("ano")),
          justificativa: String(fd.get("justificativa")),
        }),
      })
      setMessage("Inutilização enfileirada. O resultado aparece na lista abaixo.")
      setIsError(false)
      await loadInutilizacoes({ silent: true })
    } catch (err) {
      setIsError(true)
      setMessage(parseApiError(err))
    } finally {
      setSubmittingInutilizacao(false)
    }
  }

  const downloadZipLote = async () => {
    if (!empresaId) return
    const params = new URLSearchParams({
      ambiente: String(ambienteFiltro),
      incluir: zipIncluir,
    })
    if (zipDe) params.set("de", zipDe)
    if (zipAte) params.set("ate", zipAte)
    const res = await fetch(`${config.apiUrl}/v1/empresas/${empresaId}/nfe/arquivos.zip?${params}`, {
      headers: getAuthHeaders(),
    })
    if (res.status === 401) {
      redirectToLoginOnUnauthorized()
      return
    }
    if (!res.ok) {
      setIsError(true)
      const text = await res.text()
      setMessage(text || "Não foi possível gerar o ZIP. Reduza o período (máx. 100 notas).")
      return
    }
    saveBlob(await res.blob(), `nfe-${ambienteFiltro === 1 ? "prod" : "hom"}-${zipDe || "lote"}.zip`)
    setIsError(false)
    setMessage("ZIP gerado com sucesso.")
  }

  const downloadDanfe = async (doc: NfeDoc) => {
    if (!empresaId) return
    const res = await fetch(`${config.apiUrl}/v1/empresas/${empresaId}/nfe/${doc.id}/danfe`, {
      headers: getAuthHeaders(),
    })
    if (res.status === 401) {
      redirectToLoginOnUnauthorized()
      return
    }
    if (!res.ok) {
      setIsError(true)
      setMessage("Não foi possível baixar o DANFE.")
      return
    }
    saveBlob(await res.blob(), `${fileStem(doc)}.pdf`)
  }

  const empresaLabel = ctx?.empresa.nome_fantasia || ctx?.empresa.razao_social

  const feedbackBanner =
    message ? (
      <div
        role="status"
        aria-live="polite"
        className={`rounded-xl border px-4 py-3 text-sm ${
          isError ? "border-red-200 bg-red-50 text-red-900" : "border-emerald-200 bg-emerald-50 text-emerald-950"
        }`}
      >
        {message}
      </div>
    ) : null

  return (
    <div className="space-y-6">
      {feedbackBanner}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">NF-e</h1>
        <p className="mt-1 text-sm text-slate-600">
          Emissão e autorização para a <strong>empresa ativa</strong> no menu lateral. Nesta versão, apenas emitentes de{" "}
          <strong>São Paulo (UF SP)</strong>.{" "}
          <Link to="/app/nfe/faixa" className="font-medium text-brand-700 hover:underline">
            Ver a faixa da série
          </Link>
        </p>
      </div>

      {!empresaId && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Selecione uma empresa em <strong>Empresa ativa</strong> na barra lateral.
        </p>
      )}

      {ctx && (
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
            <span className="font-medium">Empresa:</span> {empresaLabel} — CNPJ {ctx.empresa.cnpj} ({ctx.empresa.uf})
          </div>

          {ctx.sefaz_simulado ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              <p className="font-semibold">Modo simulado (desenvolvimento)</p>
              <p className="mt-1">
                As notas são processadas <strong>sem chamar a SEFAZ de verdade</strong>. Serve para testar fila, storage e
                painel. Para autorização real, o administrador desativa o modo simulado nas configurações da plataforma.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
              <p className="font-semibold">Modo SEFAZ real</p>
              <p className="mt-1">
                Os envios à SEFAZ-SP dependem do certificado A1 cadastrado e do processamento em segundo plano.
              </p>
            </div>
          )}

          {!ctx.empresa_uf_ok && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              A empresa selecionada não é de SP. A emissão NF-e nesta versão exige UF SP.
            </div>
          )}

          {fila && fila.backend === "postgres" && (
            <div
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800"
              role="status"
              aria-live="polite"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="inline-flex items-center gap-2 font-medium text-slate-900">
                  {fila.worker_parece_ativo ? (
                    <>
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                      </span>
                      Processamento ativo
                    </>
                  ) : (
                    <>
                      <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                      Processamento lento
                    </>
                  )}
                </span>
                <span className="text-slate-600">Processamento automático em segundo plano.</span>
              </div>
              <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-slate-700">
                <li>
                  Jobs pendentes (sua empresa):{" "}
                  <strong>{fila.jobs_pendentes.desta_empresa ?? 0}</strong>
                  {fila.jobs_pendentes.total != null ? (
                    <span className="text-slate-500"> · plataforma: {fila.jobs_pendentes.total}</span>
                  ) : null}
                </li>
                <li>
                  Em processamento (sua empresa): <strong>{fila.jobs_processando.desta_empresa ?? 0}</strong>
                </li>
                <li>
                  Notas aguardando processamento: <strong>{fila.notas_aguardando_worker ?? 0}</strong>
                </li>
                <li>Último job concluído: {tempoRelativo(fila.ultimo_job_concluido_em)}</li>
              </ul>
              {!fila.worker_parece_ativo &&
              ((fila.jobs_pendentes.desta_empresa ?? 0) > 0 || (fila.notas_aguardando_worker ?? 0) > 0) ? (
                <p className="mt-2 text-amber-800">
                  Há itens na fila, mas nada foi processado nos últimos minutos. Aguarde ou contate o suporte.
                </p>
              ) : null}
              {notasAguardando ? (
                <p className="mt-2 inline-flex items-center gap-2 text-brand-700">
                  <Spinner className="h-3.5 w-3.5" />
                  Atualizando a lista automaticamente enquanto houver notas em fila…
                </p>
              ) : null}
            </div>
          )}

          {fila?.backend === "rabbitmq" && fila.mensagem ? (
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{fila.mensagem}</p>
          ) : null}
        </div>
      )}

      <form onSubmit={handleEmit} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">1 — Nota de demonstração</h2>
        <p className="text-sm text-slate-600">
          Gera uma NF-e de <strong>homologação</strong> com dados fixos (um produto, destinatário teste). Não precisa de arquivo
          XML. Use para testar emissão e acompanhar o status na lista.
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
          <li>Ambiente: homologação (nota sem valor fiscal).</li>
          <li>Não substitui emissão do seu ERP ou emissor fiscal.</li>
        </ul>
        <button
          type="submit"
          disabled={!empresaId || ctx?.empresa_uf_ok === false || submittingDemo}
          className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submittingDemo ? (
            <>
              <Spinner className="mr-2" />
              Enfileirando…
            </>
          ) : (
            "Gerar nota de demonstração"
          )}
        </button>
      </form>

      <form onSubmit={handleEmitXml} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">2 — Enviar XML da nota (com ou sem assinatura)</h2>
        <p className="text-sm text-slate-600">
          Use quando a nota foi montada <strong>fora</strong> do FiscalHub (ERP, emissor desktop). Envie o <strong>.xml</strong>.
          Se ainda <strong>não estiver assinado</strong>, o FiscalHub assina com o certificado A1 cadastrado (hom ou prod, conforme{" "}
          <code className="rounded bg-slate-100 px-1 text-xs">tpAmb</code> do XML) e em seguida enfileira a autorização na SEFAZ.
        </p>

        <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-700 space-y-2">
          <p className="font-medium text-slate-900">O XML precisa:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Ser NF-e layout 4.00, emitente em SP (código UF 35).</li>
            <li>
              Pode vir <strong>sem assinatura</strong> (só <code className="text-xs">infNFe</code>) ou já assinado (
              <strong>Signature</strong>). Sem assinatura exige certificado A1 na tela Certificados.
            </li>
            <li>CNPJ do emitente igual ao da empresa ativa ({ctx?.empresa.cnpj ?? "—"}).</li>
            <li>
              Ambiente no XML (<code className="rounded bg-white px-1 text-xs">tpAmb</code>): 2 = homologação, 1 = produção.
            </li>
          </ul>
        </div>

        {ctx && !ctx.sefaz_simulado && (
          <div className="text-sm text-slate-700 space-y-1">
            <p className="font-medium text-slate-900">Certificado A1 no FiscalHub (para SEFAZ real):</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                XML de homologação → cadastre certificado em{" "}
                <Link to="/app/certificados" className="text-brand-600 hover:underline">
                  Certificados → Homologação
                </Link>
                {ctx.certificado_hom ? " ✓" : " — pendente"}
              </li>
              <li>
                XML de produção →{" "}
                <Link to="/app/certificados" className="text-brand-600 hover:underline">
                  Certificados → Produção
                </Link>
                {ctx.certificado_prod ? " ✓" : " — pendente"}
              </li>
            </ul>
            <p className="text-slate-500">A senha do .pfx é informada só na tela de Certificados, não aqui.</p>
          </div>
        )}

        <input
          type="file"
          accept=".xml,application/xml,text/xml"
          onChange={handleXmlFileChange}
          disabled={submittingXml}
          className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-brand-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-brand-700 disabled:opacity-50"
          aria-label="Arquivo XML da NF-e assinada"
        />
        <button
          type="submit"
          disabled={!xmlFile || !empresaId || ctx?.empresa_uf_ok === false || submittingXml}
          className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submittingXml ? (
            <>
              <Spinner className="mr-2" />
              Lendo XML e enfileirando…
            </>
          ) : (
            "Enviar XML (assinar se necessário e autorizar)"
          )}
        </button>
      </form>

      <form onSubmit={handleInutilizar} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Inutilizar faixa de numeração</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="text-sm">
            Ambiente
            <select name="ambiente" defaultValue="2" className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5">
              <option value="2">Homologação</option>
              <option value="1">Produção</option>
            </select>
          </label>
          <label className="text-sm">
            Série
            <input name="serie" type="number" defaultValue={1} min={1} className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5" />
          </label>
          <label className="text-sm">
            Ano
            <input name="ano" type="number" defaultValue={new Date().getFullYear()} className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5" />
          </label>
          <label className="text-sm">
            Nº inicial
            <input name="numero_inicial" type="number" min={1} required className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5" />
          </label>
          <label className="text-sm">
            Nº final
            <input name="numero_final" type="number" min={1} required className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5" />
          </label>
          <label className="text-sm sm:col-span-2 lg:col-span-3">
            Justificativa
            <input name="justificativa" required minLength={15} className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5" />
          </label>
        </div>
        <button
          type="submit"
          disabled={!empresaId || submittingInutilizacao}
          className="inline-flex items-center justify-center rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
        >
          {submittingInutilizacao ? (
            <>
              <Spinner className="mr-2" />
              Enfileirando…
            </>
          ) : (
            "Inutilizar faixa"
          )}
        </button>

        <div className="border-t border-slate-200 pt-4">
          <h3 className="text-sm font-semibold text-slate-900">Inutilizações</h3>
          <p className="mt-1 text-xs text-slate-500">
            Série, faixa e protocolo. Enquanto estiver na fila, a lista atualiza sozinha.
          </p>
          {loadingInutilizacoes && inutilizacoes.length === 0 ? (
            <p className="mt-3 flex items-center gap-2 text-sm text-slate-600">
              <Spinner className="h-4 w-4" />
              Carregando…
            </p>
          ) : inutilizacoes.length === 0 ? (
            <p className="mt-3 text-sm text-slate-600">Nenhuma inutilização registrada.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Série</th>
                    <th className="px-3 py-2 font-medium">Faixa</th>
                    <th className="px-3 py-2 font-medium">Ano</th>
                    <th className="px-3 py-2 font-medium">Ambiente</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Protocolo</th>
                    <th className="px-3 py-2 font-medium">Data</th>
                    <th className="px-3 py-2 font-medium">Justificativa</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {inutilizacoes.map((row) => {
                    const naFila = row.status === "ENFILEIRADO" || row.status === "PENDENTE"
                    return (
                      <tr key={row.id} className={naFila ? "bg-amber-50/60" : ""}>
                        <td className="px-3 py-2 font-medium text-slate-900">{row.serie}</td>
                        <td className="px-3 py-2 font-mono text-slate-900">
                          {row.numero_inicial}
                          {row.numero_final !== row.numero_inicial ? ` a ${row.numero_final}` : ""}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{row.ano}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded px-2 py-0.5 text-xs font-medium ${row.ambiente === 1 ? "bg-slate-800 text-white" : "bg-amber-100 text-amber-900"}`}
                          >
                            {ambienteLabel(row.ambiente)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${statusClass(row.status)}`}>
                            {naFila ? <Spinner className="h-3 w-3" /> : null}
                            {inutilizacaoStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{row.protocolo || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{formatWhen(row.criado_em)}</td>
                        <td className="max-w-xs px-3 py-2 text-slate-600">{row.justificativa || "—"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </form>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Download em lote (ZIP)</h2>
        <p className="text-sm text-slate-600">
          Usa o ambiente selecionado na listagem abaixo. Limite de 100 notas por pedido — refine as datas se necessário.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-slate-600">
            De
            <input type="date" value={zipDe} onChange={(e) => setZipDe(e.target.value)} className="ml-2 rounded-lg border border-slate-300 px-2 py-1" />
          </label>
          <label className="text-sm text-slate-600">
            Até
            <input type="date" value={zipAte} onChange={(e) => setZipAte(e.target.value)} className="ml-2 rounded-lg border border-slate-300 px-2 py-1" />
          </label>
          <label className="text-sm text-slate-600">
            Incluir
            <select
              value={zipIncluir}
              onChange={(e) => setZipIncluir(e.target.value as "ambos" | "xml" | "danfe")}
              className="ml-2 rounded-lg border border-slate-300 px-2 py-1"
            >
              <option value="ambos">XML + DANFE</option>
              <option value="xml">Só XML</option>
              <option value="danfe">Só DANFE</option>
            </select>
          </label>
          <button
            type="button"
            disabled={!empresaId}
            onClick={downloadZipLote}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Baixar ZIP
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {feedbackBanner ? <div className="border-b border-slate-200 px-5 py-3">{feedbackBanner}</div> : null}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="font-semibold text-slate-900">Notas</h2>
            {filaAmbienteListagem ? (
              <p className="mt-0.5 text-xs text-slate-600">
                Na fila neste ambiente:{" "}
                <strong>{filaAmbienteListagem.notas_na_fila ?? 0}</strong> nota(s) ·{" "}
                <strong>{filaAmbienteListagem.jobs_pendentes ?? 0}</strong> job(s) pendente(s) ·{" "}
                <strong>{filaAmbienteListagem.jobs_processando ?? 0}</strong> em processamento
              </p>
            ) : null}
          </div>
          <div className="flex gap-1 rounded-lg border border-slate-200 p-0.5" role="group" aria-label="Filtrar ambiente">
            <div
              className={`inline-flex items-stretch overflow-hidden rounded-md ${ambienteFiltro === 1 ? "bg-slate-800 text-white" : ""}`}
            >
              <button
                type="button"
                aria-pressed={ambienteFiltro === 1}
                onClick={() => handleAmbienteFiltro(1)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${ambienteFiltro === 1 ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >
                Produção
                {badgeProd ? (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${ambienteFiltro === 1 ? "bg-white/20 text-white" : "bg-amber-100 text-amber-900"}`}
                  >
                    {badgeProd}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                title="Atualizar produção"
                aria-label="Atualizar listagem e fila de produção"
                disabled={!empresaId || atualizandoAmbiente !== null}
                onClick={() => handleAtualizarAmbiente(1)}
                className={`inline-flex items-center justify-center border-l px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-50 ${
                  ambienteFiltro === 1
                    ? "border-white/20 text-white hover:bg-white/10"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                }`}
              >
                {atualizandoAmbiente === 1 ? <Spinner className="h-3.5 w-3.5" /> : <RefreshIcon />}
              </button>
            </div>
            <div
              className={`inline-flex items-stretch overflow-hidden rounded-md ${ambienteFiltro === 2 ? "bg-amber-100 text-amber-950" : ""}`}
            >
              <button
                type="button"
                aria-pressed={ambienteFiltro === 2}
                onClick={() => handleAmbienteFiltro(2)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${ambienteFiltro === 2 ? "text-amber-950" : "text-slate-600 hover:bg-slate-50"}`}
              >
                Homologação
                {badgeHom ? (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${ambienteFiltro === 2 ? "bg-amber-200 text-amber-950" : "bg-amber-100 text-amber-900"}`}
                  >
                    {badgeHom}
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                title="Atualizar homologação"
                aria-label="Atualizar listagem e fila de homologação"
                disabled={!empresaId || atualizandoAmbiente !== null}
                onClick={() => handleAtualizarAmbiente(2)}
                className={`inline-flex items-center justify-center border-l px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-50 ${
                  ambienteFiltro === 2
                    ? "border-amber-300/80 text-amber-900 hover:bg-amber-200/60"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                }`}
              >
                {atualizandoAmbiente === 2 ? <Spinner className="h-3.5 w-3.5" /> : <RefreshIcon />}
              </button>
            </div>
          </div>
        </div>
        {loading && docs.length === 0 ? (
          <p className="flex items-center gap-2 p-5 text-sm text-slate-600">
            <Spinner className="h-4 w-4" />
            Carregando…
          </p>
        ) : docs.length === 0 ? (
          <p className="p-5 text-sm text-slate-600">
            Nenhuma nota de {ambienteFiltro === 1 ? "produção" : "homologação"}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Série</th>
                  <th className="px-4 py-2 font-medium">Nº NF</th>
                  <th className="px-4 py-2 font-medium">Ambiente</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Fila</th>
                  <th className="px-4 py-2 font-medium">Data</th>
                  <th className="px-4 py-2 font-medium">Protocolo</th>
                  <th className="px-4 py-2 font-medium">SEFAZ</th>
                  <th className="px-4 py-2 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {docs.map((doc) => {
                  const failed = doc.status === "REJEITADO" || doc.status === "ERRO" || doc.status === "FALHA_COMUNICACAO"
                  const sefaz = [doc.codigo_status_sefaz, !failed ? doc.motivo_status_sefaz : null].filter(Boolean).join(" — ")
                  const filaLabel = filaJobLabel(doc.job_fila)
                  const evResumo = eventoFiscalResumo(doc)
                  const cancelInfo = cancelamentoDetalhes(doc)
                  const rowHighlight =
                    doc.job_fila ||
                    doc.evento_fila ||
                    cancelamentoPendente(doc) ||
                    doc.status === "ENFILEIRADO" ||
                    doc.status === "PROCESSANDO"
                  return (
                    <tr
                      key={doc.id}
                      className={`align-top ${rowHighlight ? "bg-amber-50/60" : ""}`}
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">{doc.serie}</td>
                      <td className="px-4 py-3 font-mono text-slate-900">{String(doc.numero).padStart(9, "0")}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${doc.ambiente === 1 ? "bg-slate-800 text-white" : "bg-amber-100 text-amber-900"}`}
                        >
                          {ambienteLabel(doc.ambiente)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusClass(doc.status)}`}>
                          {statusLabel(doc)}
                        </span>
                        {evResumo ? (
                          <p
                            className={`mt-1 max-w-xs text-xs ${
                              evResumo.tone === "err"
                                ? "text-red-700"
                                : evResumo.tone === "wait"
                                  ? "text-amber-800"
                                  : "text-slate-600"
                            }`}
                          >
                            {evResumo.tone === "wait" ? (
                              <span className="mr-1 inline-flex align-middle">
                                <Spinner className="h-3 w-3" />
                              </span>
                            ) : null}
                            {evResumo.text}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        {filaLabel ? (
                          <span
                            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${filaJobClass(doc.job_fila)}`}
                          >
                            {doc.job_fila === "processando" ? <Spinner className="h-3 w-3" /> : null}
                            {filaLabel}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">{formatWhen(doc.processado_em || doc.criado_em)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{doc.protocolo || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">
                        <p>{sefaz || "—"}</p>
                        {failed && doc.motivo_status_sefaz ? (
                          <p className="mt-1 max-w-sm text-xs text-red-700">{doc.motivo_status_sefaz}</p>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => downloadXml(doc)} className="text-brand-600 hover:underline">
                          XML
                        </button>
                        {doc.pode_reenviar ? (
                          <button
                            type="button"
                            onClick={() => handleReenviar(doc)}
                            disabled={reenviandoId === doc.id}
                            className="ml-3 text-brand-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                          >
                            {reenviandoId === doc.id ? "Enfileirando…" : "Tentar de novo"}
                          </button>
                        ) : null}
                        {doc.status === "AUTORIZADO" || doc.status === "CANCELADO" ? (
                          <button type="button" onClick={() => downloadDanfe(doc)} className="ml-3 text-brand-600 hover:underline">
                            DANFE
                          </button>
                        ) : null}
                        {doc.chave_acesso ? (
                          <div className="mt-1 flex max-w-xs items-start gap-1">
                            <p className="min-w-0 flex-1 break-all text-xs text-slate-500">{doc.chave_acesso}</p>
                            <button
                              type="button"
                              onClick={() => handleCopyChave(doc)}
                              className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              aria-label="Copiar chave de acesso"
                              title={copiedChaveId === doc.id ? "Copiado!" : "Copiar chave de acesso"}
                            >
                              <CopyIcon className={copiedChaveId === doc.id ? "text-emerald-600" : undefined} />
                            </button>
                          </div>
                        ) : null}
                        {doc.status === "AUTORIZADO" ? (
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            <button
                              type="button"
                              disabled={cancelamentoPendente(doc)}
                              onClick={() => openCancelDialog(doc)}
                              className="text-red-700 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                            >
                              {cancelamentoPendente(doc) ? "Cancelando…" : "Cancelar"}
                            </button>
                            <button type="button" onClick={() => handleCce(doc)} className="text-brand-600 hover:underline">
                              CC-e
                            </button>
                            <button type="button" onClick={() => handleSincronizar(doc)} className="text-slate-700 hover:underline">
                              Consultar SEFAZ
                            </button>
                          </div>
                        ) : doc.status === "CANCELADO" ? (
                          <div className="mt-2 max-w-xs rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-900">
                            <p className="font-medium">Cancelamento na SEFAZ</p>
                            {cancelInfo ? (
                              <ul className="mt-1 space-y-0.5 text-red-800">
                                {cancelInfo.protocolo ? (
                                  <li>
                                    <span className="text-red-700">Protocolo:</span> {cancelInfo.protocolo}
                                  </li>
                                ) : null}
                                {cancelInfo.quando ? (
                                  <li>
                                    <span className="text-red-700">Registrado:</span> {formatWhen(cancelInfo.quando)}
                                  </li>
                                ) : null}
                                {cancelInfo.justificativa ? (
                                  <li>
                                    <span className="text-red-700">Justificativa:</span> {cancelInfo.justificativa}
                                  </li>
                                ) : null}
                                {cancelInfo.motivoSefaz ? (
                                  <li>
                                    <span className="text-red-700">SEFAZ:</span> {cancelInfo.motivoSefaz}
                                  </li>
                                ) : null}
                                {cancelInfo.statusEvento && cancelInfo.statusEvento !== "REGISTRADO" ? (
                                  <li className="text-amber-900">Evento no FiscalHub: {cancelInfo.statusEvento}</li>
                                ) : null}
                              </ul>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => handleSincronizar(doc)}
                              className="mt-2 text-red-800 underline hover:text-red-950"
                            >
                              Consultar SEFAZ
                            </button>
                            <p className="mt-1 text-red-700">O DANFE exibe carimbo &quot;NF-e CANCELADA&quot;.</p>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 text-sm text-slate-600">
          <p className="inline-flex items-center gap-2">
            {loading && docs.length > 0 ? <Spinner className="h-3.5 w-3.5 text-slate-400" aria-hidden /> : null}
            {total === 0
              ? "0 notas"
              : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || loading}
              className="rounded-lg border border-slate-200 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="min-w-16 text-center">
              {page} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page * PAGE_SIZE >= total || loading}
              className="rounded-lg border border-slate-200 px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>

      {cancelTarget ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-nfe-title"
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
            <h2 id="cancel-nfe-title" className="text-lg font-semibold text-slate-900">
              Cancelar NF-e {cancelTarget.serie}/{String(cancelTarget.numero).padStart(9, "0")}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              A justificativa é enviada à SEFAZ (mínimo 15 caracteres). O cancelamento só conclui após a confirmação da
              SEFAZ.
            </p>
            <label className="mt-4 block text-sm font-medium text-slate-800">
              Justificativa
              <textarea
                value={cancelJustificativa}
                onChange={(e) => setCancelJustificativa(e.target.value)}
                rows={4}
                minLength={15}
                maxLength={255}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Ex.: Nota emitida com dados incorretos do pedido"
                disabled={cancelSubmitting}
              />
            </label>
            <p className="mt-1 text-xs text-slate-500">{cancelJustificativa.trim().length}/15 caracteres mínimos</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCancelDialog}
                disabled={cancelSubmitting}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleConfirmCancelar}
                disabled={cancelSubmitting || cancelJustificativa.trim().length < 15}
                className="inline-flex items-center rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {cancelSubmitting ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Enfileirando…
                  </>
                ) : (
                  "Confirmar cancelamento"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
